<template>
    <div
        class="d-flex justify-content-between align-items-center w-100"
    >
        <i18n path="baseTablePagination.showing" tag="div" class="text-muted">
            <template #start><span class="text-dark font-weight-bold">{{startItem}}</span></template>
            <template #end><span class="text-dark font-weight-bold">{{endItem}}</span></template>
            <template #total><span class="text-dark font-weight-bold">{{totalItems}}</span></template>
            <template #itemName>{{finalItemName}}</template>
        </i18n>
        <div v-if="showPerPage && totalItems > 5" class="d-flex align-items-center">
            <div class="text-muted text-nowrap mr-1">{{ $t('baseTablePagination.perPage') }}</div>
            <b-form-select 
                :value="perPage" 
                @input="updatePerPage" 
                :options="perPageOptions" 
                size="sm"
            ></b-form-select>                
        </div>
        <div class="d-flex align-items-center">
            <i18n v-if="!hidePageNumbers" path="baseTablePagination.pageOf" tag="div" class="text-muted mr-2">
                <template #current><span class="text-dark font-weight-bold">{{currentPage}}</span></template>
                <template #total><span class="text-dark font-weight-bold">{{totalPages}}</span></template>
            </i18n>
            <b-pagination
                :value="currentPage"
                @input="updateCurrentPage"
                :total-rows="totalItems"
                :per-page="perPage"
                align="right"
                size="sm"
                class="my-0"
            ></b-pagination>
        </div>
    </div>
</template>

<script>

/* use like this:
<base-table-pagination
    :current-page.sync="currentPage"
    :per-page.sync="perPage"
    :total-items="contacts.length"
    show-per-page
    @update:perPage="getList"   <!-- use these if you want additional triggers on the update events -->
    @update:currentPage="getList"
></base-table-pagination>
*/

import { splTableDefaults } from '../js/custom-vue-mixins';


export default {
    name: 'BaseTablePagination',
    components: {},
    i18n: {
        messages: {
            en: {
                baseTablePagination: {
                    showing: 'Showing {start} - {end} of {total} {itemName}',
                    perPage: 'Per Page',
                    pageOf: 'Page {current} of {total}',
                    items: 'items',
                },
            },
            es: {
                baseTablePagination: {
                    showing: 'Mostrando {start} - {end} de {total} {itemName}',
                    perPage: 'Por Página',
                    pageOf: 'Página {current} de {total}',
                    items: 'elementos',
                },
            },
            pt: {
                baseTablePagination: {
                    showing: 'Mostrando {start} - {end} de {total} {itemName}',
                    perPage: 'Por Página',
                    pageOf: 'Página {current} de {total}',
                    items: 'itens',
                },
            },
            'zh-Hans': {
                baseTablePagination: {
                    showing: '显示 {start} - {end}，共 {total} 个{itemName}',
                    perPage: '每页',
                    pageOf: '第 {current} 页，共 {total} 页',
                    items: '项目',
                },
            },
            'zh-Hant': {
                baseTablePagination: {
                    showing: '顯示 {start} - {end}，共 {total} 個{itemName}',
                    perPage: '每頁',
                    pageOf: '第 {current} 頁，共 {total} 頁',
                    items: '項目',
                },
            },
        },
    },
    props: {
        currentPage: {  // use .sync in parent!
            type: Number,
            required: true,
        },
        perPage: {
            type: Number,
            required: true,
            validator: function (value) {
                return splTableDefaults.perPageOptions.includes(value);
            },
        },
        totalItems: {  // qty of items 
            type: Number,
            required: true,
        },
        showPerPage: {  // show select box for per page qty?
            type: Boolean,
            default: false,
        },
        perPageOptions: {
            type: Array,
            default: function() {
                return splTableDefaults.perPageOptions;
            }
        },
        itemName: {  // plural version of item name, like 'contacts' or 'products'
            type: String,
            default: '',  // when empty, the translated default 'items' is used (see finalItemName)
        },
        hidePageNumbers: {  // if true, hides "Page 1 of X"
            type: Boolean,
            default: false,
        }
    },
    data: function() {
        return {
            errorMsg: 'Error',
            error: false,
        }
    },
    computed: {
        finalItemName: function() {
            // the itemName prop (parent override) or the translated default
            return this.itemName || this.$t('baseTablePagination.items');
        },
        startItem: function() {
            return this.currentPage * this.perPage - this.perPage + 1;
        },
        endItem: function() {
            let maxPossible = this.currentPage * this.perPage;
            if (this.totalItems < maxPossible) return this.totalItems;
            return maxPossible;
        },
        totalPages: function() {
            let fullPages = Math.floor(this.totalItems / this.perPage);
            let extra = this.totalItems % this.perPage;  // returns the remainder of x / y
            let total = fullPages + (extra ? 1 : 0);
            return total;
        }
    },
    methods: {
        // both of these use .sync method of 2-way sync on props
        updateCurrentPage: function(currentPage) {
            this.$emit('update:currentPage', currentPage);
        },
        updatePerPage: function(perPage) {
            this.$emit('update:perPage', perPage);
        },
    }
}
</script>

<style>

</style>